import { registerSuperProperties } from "@Light/app/analytics";
import { createContext, ReactNode, useContext, useEffect } from "react";
import { FeatureFlag } from "./features";

// Add new experiments here. All experiments should have a "control" case for
// compatibility with Posthog experiments.
export type Experiments = {
  // "your-experiment": "control" | "variant";
};

export type ExperimentName = keyof Experiments;

export type ExperimentCase<N extends ExperimentName = ExperimentName> =
  Experiments[N];

export type ExperimentCases = {
  [N in ExperimentName]: ExperimentCase<N>;
};

/** Resolves which case each experiment is in, given the raw backend flags. */
export function adaptExperiments(_flags: FeatureFlag[]): ExperimentCases {
  return {
    // "your-experiment": flags.includes("YOUR_FLAG") ? "variant" : "control",
  };
}

export const ExperimentContext = createContext<ExperimentCases>(
  adaptExperiments([]),
);

type ProviderProps = {
  featureFlags: FeatureFlag[];
  children: ReactNode;
};

function Provider({ featureFlags, children }: ProviderProps) {
  const experiments = adaptExperiments(featureFlags);

  // Register every experiment as a property on subsequent posthog events.
  useEffect(() => {
    const properties: Record<string, string> = {};
    for (const name of Object.keys(experiments) as ExperimentName[]) {
      properties[`$feature/${name}`] = experiments[name];
    }
    registerSuperProperties(properties);
  }, [experiments]);

  return (
    <ExperimentContext.Provider value={experiments}>
      {children}
    </ExperimentContext.Provider>
  );
}

/** Returns the user's case for a given experiment. */
export function useExperiment<N extends ExperimentName>(
  name: N,
): ExperimentCase<N> {
  return useContext(ExperimentContext)[name];
}

type GateProps<N extends ExperimentName> = {
  experiment: N;
  case: ExperimentCase<N>;
  children: ReactNode;
};

/** Renders children only when the user is in the given experiment case. */
function Gate<N extends ExperimentName>({
  experiment,
  case: expected,
  children,
}: GateProps<N>) {
  return useExperiment(experiment) === expected ? <>{children}</> : null;
}

export const Experiment = {
  Provider,
  Gate,
};
